import React, { useState, useEffect } from 'react';
import authApi, { PlanResponse, CommunityPlanAdminRequest } from '../../../services/authApi';
import { toast } from 'react-toastify';
import '../AdminTutorialsPage.css';
import './AdminPlansTab.css';

interface PlanForm {
  planName: string;
  durationMonths: number;
  price: string;
  originalPrice: string;
  discountPercent: number;
  description: string;
  featuresDescription: string;
  isActive: boolean;
}

const BLANK: PlanForm = {
  planName: '', durationMonths: 1, price: '', originalPrice: '',
  discountPercent: 0, description: '', featuresDescription: '', isActive: true,
};

const durationLabel = (months: number) => {
  if (months === 1) return '1 Month';
  if (months === 12) return '1 Year';
  if (months % 12 === 0) return `${months / 12} Years`;
  return `${months} Months`;
};

const AdminPlansTab: React.FC = () => {
  const [plans, setPlans]           = useState<PlanResponse[]>([]);
  const [loading, setLoading]       = useState(true);
  const [modalOpen, setModalOpen]   = useState(false);
  const [editing, setEditing]       = useState<PlanResponse | null>(null);
  const [form, setForm]             = useState<PlanForm>(BLANK);
  const [saving, setSaving]         = useState(false);
  const [confirmId, setConfirmId]   = useState<number | null>(null);

  useEffect(() => { load(); }, []);

  const load = () => {
    setLoading(true);
    authApi.adminGetCommunityPlans()
      .then((r) => setPlans(r.data || []))
      .catch(() => toast.error('Failed to load plans'))
      .finally(() => setLoading(false));
  };

  const openCreate = () => {
    setEditing(null);
    setForm(BLANK);
    setModalOpen(true);
  };

  const openEdit = (p: PlanResponse) => {
    setEditing(p);
    setForm({
      planName: p.planName,
      durationMonths: p.durationMonths,
      price: String(p.price),
      originalPrice: String(p.originalPrice),
      discountPercent: p.discountPercent ?? 0,
      description: p.description || '',
      featuresDescription: (p.features || []).join(', '),
      isActive: p.isActive,
    });
    setModalOpen(true);
  };

  const closeModal = () => { setModalOpen(false); setEditing(null); setForm(BLANK); };

  const set = (field: keyof PlanForm, value: any) =>
    setForm((f) => {
      const updated = { ...f, [field]: value };
      if (field === 'price' || field === 'originalPrice') {
        const price = Number(field === 'price' ? value : updated.price);
        const orig  = Number(field === 'originalPrice' ? value : updated.originalPrice);
        if (orig > 0 && price >= 0 && orig > price) {
          updated.discountPercent = Math.round(((orig - price) / orig) * 100);
        } else {
          updated.discountPercent = 0;
        }
      }
      return updated;
    });

  const save = async () => {
    if (!form.planName.trim()) { toast.error('Plan name is required'); return; }
    if (!form.price || isNaN(Number(form.price)) || Number(form.price) < 0) {
      toast.error('Valid price is required'); return;
    }
    if (!form.originalPrice || isNaN(Number(form.originalPrice)) || Number(form.originalPrice) < 0) {
      toast.error('Valid original price is required'); return;
    }
    if (form.durationMonths < 1) { toast.error('Duration must be at least 1 month'); return; }

    setSaving(true);
    try {
      const payload: CommunityPlanAdminRequest = {
        planName: form.planName.trim(),
        durationMonths: form.durationMonths,
        price: Math.round(Number(form.price)),
        originalPrice: Math.round(Number(form.originalPrice)),
        discountPercent: form.discountPercent || 0,
        description: form.description.trim() || undefined,
        featuresDescription: form.featuresDescription.trim() || undefined,
        isActive: form.isActive,
      };
      if (editing) {
        await authApi.adminUpdateCommunityPlan(editing.id, payload);
        toast.success('Plan updated');
      } else {
        await authApi.adminCreateCommunityPlan(payload);
        toast.success('Plan created');
      }
      closeModal();
      load();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const deactivate = async (id: number) => {
    try {
      await authApi.adminDeactivateCommunityPlan(id);
      toast.success('Plan deactivated');
      setPlans((prev) => prev.map((p) => p.id === id ? { ...p, isActive: false } : p));
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Deactivate failed');
    } finally {
      setConfirmId(null);
    }
  };

  const reactivate = async (id: number) => {
    try {
      await authApi.adminReactivateCommunityPlan(id);
      toast.success('Plan activated');
      setPlans((prev) => prev.map((p) => p.id === id ? { ...p, isActive: true } : p));
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Activate failed');
    }
  };

  return (
    <div style={{ padding: 0 }}>
      {/* Header */}
      <div className="apl-header">
        <div>
          <p className="tool-eyebrow">Billing</p>
          <h1 className="tool-title" style={{ marginBottom: 2 }}>Plan Management</h1>
          <p className="tool-subtitle">Create and manage COMMUNITY subscription plans.</p>
        </div>
        <button className="adm-btn adm-btn-primary" onClick={openCreate}>+ Add Plan</button>
      </div>

      {/* Content */}
      {loading ? (
        <div className="apl-loading">
          <div className="apl-dot" /><div className="apl-dot" /><div className="apl-dot" />
        </div>
      ) : plans.length === 0 ? (
        <div className="apl-empty">
          <span className="apl-empty-icon">💳</span>
          No plans yet.<br />Click <strong>+ Add Plan</strong> to create your first subscription plan.
        </div>
      ) : (
        <div className="apl-grid">
          {plans.map((p) => {
            const features = p.features || [];
            const hasDiscount = p.discountPercent > 0;
            const hasSavings = p.originalPrice > p.price;

            return (
              <div key={p.id} className={`apl-card ${!p.isActive ? 'apl-card--inactive' : ''}`}>
                {/* Name + duration + discount */}
                <div className="apl-name-row">
                  <span className="apl-name">{p.planName}</span>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    {!p.isActive && <span className="apl-status-badge apl-status-inactive">Disabled</span>}
                    {p.isActive && <span className="apl-status-badge apl-status-active">Live</span>}
                    {hasDiscount && <span className="apl-discount-badge">{p.discountPercent}% OFF</span>}
                    <span className="apl-duration">{durationLabel(p.durationMonths)}</span>
                  </div>
                </div>

                {/* Price */}
                <div className="apl-price">
                  <span className="apl-price-symbol">₹</span>
                  {Number(p.price).toLocaleString('en-IN')}
                </div>

                {hasSavings && (
                  <div className="apl-original-price">
                    was ₹{Number(p.originalPrice).toLocaleString('en-IN')}
                  </div>
                )}

                <div className="apl-desc">{p.description || ''}</div>

                <div className="apl-divider" />

                {/* Features */}
                {features.length > 0 ? (
                  <ul className="apl-features">
                    {features.map((f, i) => (
                      <li key={i} className="apl-feature">
                        <CheckIcon />
                        {f.trim()}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="apl-no-features">No features listed.</p>
                )}

                {/* Actions */}
                <div className="apl-actions">
                  <button className="adm-btn adm-btn-ghost" onClick={() => openEdit(p)}>Edit</button>
                  {p.isActive ? (
                    <button className="adm-btn adm-btn-danger" onClick={() => setConfirmId(p.id)}>Disable</button>
                  ) : (
                    <button className="adm-btn adm-btn-enable" onClick={() => reactivate(p.id)}>Enable</button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create / Edit Modal */}
      {modalOpen && (
        <div className="adm-modal-overlay" onClick={closeModal}>
          <div className="adm-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 520 }}>
            <div className="adm-modal-header">
              <h3>{editing ? 'Edit Plan' : 'New Plan'}</h3>
              <button className="adm-modal-close" onClick={closeModal}>✕</button>
            </div>
            <div className="adm-modal-body">
              <div className="adm-field">
                <label>Plan Name</label>
                <input
                  value={form.planName}
                  onChange={(e) => set('planName', e.target.value)}
                  placeholder="e.g. Pro Monthly"
                />
              </div>
              <div className="adm-field-row">
                <div className="adm-field adm-field--sm">
                  <label>Duration (months)</label>
                  <input
                    type="number"
                    min={1}
                    value={form.durationMonths}
                    onChange={(e) => set('durationMonths', Number(e.target.value))}
                  />
                </div>
                <div className="adm-field adm-field--grow">
                  <label>Price (₹)</label>
                  <input
                    type="number"
                    min={0}
                    step="1"
                    value={form.price}
                    onChange={(e) => set('price', e.target.value.replace(/\./g, ''))}
                    onKeyDown={(e) => e.key === '.' && e.preventDefault()}
                    placeholder="e.g. 499"
                  />
                </div>
                <div className="adm-field adm-field--grow">
                  <label>Original Price (₹)</label>
                  <input
                    type="number"
                    min={0}
                    step="1"
                    value={form.originalPrice}
                    onChange={(e) => set('originalPrice', e.target.value.replace(/\./g, ''))}
                    onKeyDown={(e) => e.key === '.' && e.preventDefault()}
                    placeholder="e.g. 999"
                  />
                </div>
              </div>
              <div className="adm-field">
                <label>Discount % <span className="adm-label-sub">(auto-calculated, editable)</span></label>
                <input
                  type="number"
                  min={0}
                  max={100}
                  value={form.discountPercent}
                  onChange={(e) => set('discountPercent', Number(e.target.value))}
                />
              </div>
              <div className="adm-field">
                <label>Description <span className="adm-label-sub">(shown on card)</span></label>
                <input
                  value={form.description}
                  onChange={(e) => set('description', e.target.value)}
                  placeholder="Short tagline, e.g. Everything. Cancel any time."
                />
              </div>
              <div className="adm-field">
                <label>Features <span className="adm-label-sub">(comma-separated)</span></label>
                <textarea
                  rows={3}
                  value={form.featuresDescription}
                  onChange={(e) => set('featuresDescription', e.target.value)}
                  placeholder="Full courses, Live screeners, Community forum"
                />
              </div>
              <div className="adm-field adm-toggle-row" style={{ paddingBottom: 4 }}>
                <label className="adm-toggle-label">
                  <input
                    type="checkbox"
                    checked={form.isActive}
                    onChange={(e) => set('isActive', e.target.checked)}
                  />
                  Active (visible on pricing page)
                </label>
              </div>
            </div>
            <div className="adm-modal-footer">
              <button className="adm-btn adm-btn-ghost" onClick={closeModal}>Cancel</button>
              <button className="adm-btn adm-btn-primary" onClick={save} disabled={saving}>
                {saving ? 'Saving…' : editing ? 'Save Changes' : 'Create Plan'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Deactivate confirmation */}
      {confirmId !== null && (
        <div className="adm-modal-overlay" onClick={() => setConfirmId(null)}>
          <div className="adm-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 380 }}>
            <div className="adm-modal-header">
              <h3>Deactivate Plan</h3>
              <button className="adm-modal-close" onClick={() => setConfirmId(null)}>✕</button>
            </div>
            <div className="adm-modal-body">
              <p style={{ fontSize: 14, color: 'var(--text)', lineHeight: 1.6 }}>
                This plan will no longer appear on the pricing page. Existing active subscriptions are not affected.
              </p>
            </div>
            <div className="adm-modal-footer">
              <button className="adm-btn adm-btn-ghost" onClick={() => setConfirmId(null)}>Cancel</button>
              <button className="adm-btn adm-btn-danger" onClick={() => deactivate(confirmId!)}>
                Yes, Deactivate
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPlansTab;

function CheckIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}
